import { getGlobalConfigElement } from '../../utils/get-global-config-element';
import { PageContextStory, Story } from '../build/types';
import { NavigationService } from '../navigation';
import { StoryblokService } from '../storyblok';
import { TextDirection } from './types';

export class LanguageService {
  static readThisArticleIn = {
    en: 'Read this article in English',
    de: 'Lesen Sie diesen Artikel auf Deutsch',
    fr: 'Lire cet article en Français',
    es: 'Lea este artículo en Español',
    cn: '点击查看中文版',
  };

  static getDefaultLocale(): string {
    return process.env.GATSBY_STORYBLOK_SPACE_DEFAULT_LANGUAGE;
  }

  static async getSpaceLanguages(): Promise<string[]> {
    const allLinks = await StoryblokService.getLinks('');
    return [...new Set(allLinks.map(({ slug }) => this.getLocaleFromSlug(slug)))] as string[];
  }

  static getLocaleFromSlug(slug: string): string {
    const sanitizedSlug = slug?.endsWith('/') ? slug : `${slug}/`;
    const languageCodeMatches = (
      `/${sanitizedSlug}`.replace('//', '/').match(/^\/([\w]{2})-([\w]{2})\/|^\/([\w]{2})\//) ?? []
    ).map((match) => match?.split('/').join(''));
    return languageCodeMatches[0] || this.getDefaultLocale();
  }

  static isPageDefaultLanguage(locale?: string): boolean {
    if (!locale && typeof document === 'undefined') {
      return true;
    }
    return this.getDefaultLocale() === (locale || document.documentElement.lang);
  }

  static getActiveLanguage(): string {
    if (typeof document === 'undefined') {
      return this.getDefaultLocale();
    }
    const actualPagePath = NavigationService.getCurrentPagePath();
    return this.getLocaleFromSlug(actualPagePath);
  }

  static getParsedLocale(locale: string): string {
    return locale === 'default' ? this.getDefaultLocale() : locale;
  }

  static getLocaleAwareLink(path: string, locale: string): string {
    if (!path) return '';

    const pathTokens = this.getPathTokens(this.removeLeadingSlash(path));
    const [firstPathElement, ...rest] = pathTokens;

    if (
      this.isLocale(firstPathElement) &&
      this.isSpaceSingleLanguage()
      // && !BuildService.isClientOnlyPath(path)
    ) {
      return `/${pathTokens.join('/')}`;
    }

    const pathWithoutLocale = this.isLocale(firstPathElement) ? rest : pathTokens;
    const localizedPath = this.isPageDefaultLanguage(locale) ? pathWithoutLocale : [locale, ...pathWithoutLocale];

    return `/${localizedPath.join('/')}`;
  }

  static isLocale(locale: string): boolean {
    const localeRegex = /^[A-Za-z]{2}$/;
    return localeRegex.test(locale);
  }

  static getAdditionalLanguages(): Array<string> {
    return Object.keys(this.readThisArticleIn);
  }

  static getHomePageUrl(homeStory: Story<PageContextStory>): string {
    const fallbackPath = '/';
    if (!homeStory) return fallbackPath;
    const { full_slug: slug } = homeStory;
    const lang = this.getLocaleFromSlug(slug);
    const path = lang === this.getDefaultLocale() ? fallbackPath : slug;
    return this.getLocaleAwareLink(path, this.getParsedLocale(lang));
  }

  private static getPathTokens(path: string, separator = '/'): string[] {
    return path ? path.split(separator) : [];
  }

  private static isSpaceSingleLanguage(): boolean {
    if (typeof document === 'undefined') {
      return true;
    }
    const globalConfigElement = getGlobalConfigElement();
    const languages = globalConfigElement?.dataset.languages || '[]';
    return JSON.parse(languages).length <= 1;
  }

  private static removeLeadingSlash(path: string): string {
    return path.startsWith('/') ? path.substring(1) : path;
  }

  static getRightToLeftLanguageList(): string[] {
    return (process.env.GATSBY_RTL_LANGUAGE_CODES || '').split(',').map((code) => code.trim().toLowerCase());
  }

  /**
   * @param languageCode - The language code to check, e.g. 'en', 'de', 'ar'
   * @returns The value for the languages html dir attribute
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/dir
   */
  static getDirectionality(languageCode: string): TextDirection {
    return this.getRightToLeftLanguageList().includes(languageCode?.trim().toLowerCase())
      ? TextDirection.RTL
      : TextDirection.LTR;
  }

  /**
   * @returns True if the language is right-to-left, false otherwise
   */
  static isRightToLeft(): boolean {
    const locale = this.getActiveLanguage();
    return this.getDirectionality(locale) === TextDirection.RTL;
  }
}
